import type { IdentitySchema, IdentityField, IdentityTemplate } from '@webforge-survey/shared';
import { Icon, IconButton } from '@/components/ui';
import {
  TEMPLATE_LABELS,
  TEMPLATE_DESCRIPTIONS,
  schemaForTemplate,
} from '@/lib/identity-templates';

/**
 * Éditeur d'identitySchema (champs d'identité collectés en mode optional/required).
 * Choix d'un template prédéfini OU champs personnalisés. Contrôlé : remonte un
 * IdentitySchema complet via onChange.
 */
const TEMPLATES: IdentityTemplate[] = ['minimal', 'email', 'extended', 'custom'];

export function IdentitySchemaEditor({
  schema,
  onChange,
  disabled = false,
}: {
  schema: IdentitySchema;
  onChange: (next: IdentitySchema) => void;
  disabled?: boolean;
}): JSX.Element {
  const template = schema.template;
  const customFields = schema.template === 'custom' ? schema.fields ?? [] : [];

  const setTemplate = (t: IdentityTemplate): void => {
    onChange(schemaForTemplate(t, t === 'custom' ? (customFields.length ? customFields : defaultCustom()) : []));
  };

  const updateField = (i: number, patch: Partial<IdentityField>): void => {
    const fields = [...customFields];
    fields[i] = { ...fields[i]!, ...patch };
    onChange({ template: 'custom', fields });
  };
  const addField = (): void => {
    onChange({
      template: 'custom',
      fields: [...customFields, { name: `champ${customFields.length + 1}`, label: 'Nouveau champ', type: 'text', required: false }],
    });
  };
  const removeField = (i: number): void => {
    onChange({ template: 'custom', fields: customFields.filter((_, idx) => idx !== i) });
  };

  return (
    <fieldset className="space-y-3" disabled={disabled}>
      <legend className="sr-only">Schéma d'identité</legend>
      <div className="space-y-2">
        {TEMPLATES.map((t) => {
          const checked = template === t;
          return (
            <label key={t} className={`option-card ${checked ? 'option-card-selected' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
              <input
                type="radio"
                name="identity-template"
                value={t}
                checked={checked}
                onChange={() => setTemplate(t)}
                disabled={disabled}
                className="mt-1"
              />
              <div className="text-sm">
                <strong>{TEMPLATE_LABELS[t]}</strong>
                <span className="block text-text-muted">{TEMPLATE_DESCRIPTIONS[t]}</span>
              </div>
            </label>
          );
        })}
      </div>

      {template === 'custom' && (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Champs personnalisés</h4>
            <button type="button" className="btn-secondary btn-sm" onClick={addField} disabled={disabled}>
              + Ajouter un champ
            </button>
          </div>
          {customFields.length === 0 && (
            <p className="text-xs text-text-muted">Ajoutez au moins un champ.</p>
          )}
          {customFields.map((f, i) => (
            <div key={i} className="grid grid-cols-[1fr,1fr,auto,auto,auto] gap-2 items-end bg-bg-deep/30 rounded-lg p-2">
              <div>
                <label className="field-label text-xs">name</label>
                <input
                  className="input-base font-mono text-xs"
                  value={f.name}
                  onChange={(e) => updateField(i, { name: e.target.value })}
                  placeholder="email"
                />
              </div>
              <div>
                <label className="field-label text-xs">Libellé</label>
                <input
                  className="input-base text-xs"
                  value={f.label}
                  onChange={(e) => updateField(i, { label: e.target.value })}
                  placeholder="Adresse e-mail"
                />
              </div>
              <div>
                <label className="field-label text-xs">Type</label>
                <select
                  className="input-base text-xs"
                  value={f.type}
                  onChange={(e) => updateField(i, { type: e.target.value as IdentityField['type'] })}
                >
                  <option value="text">Texte</option>
                  <option value="email">E-mail</option>
                  <option value="integer">Nombre</option>
                </select>
              </div>
              <label className="flex items-center gap-1.5 text-xs pb-2.5">
                <input
                  type="checkbox"
                  checked={f.required}
                  onChange={(e) => updateField(i, { required: e.target.checked })}
                />
                Requis
              </label>
              <div className="pb-1.5">
                <IconButton label="Supprimer le champ" tone="danger" onClick={() => removeField(i)}>
                  <Icon.Trash />
                </IconButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}

function defaultCustom(): IdentityField[] {
  return [{ name: 'email', label: 'Adresse e-mail', type: 'email', required: true }];
}
